import { Input } from '@commenda/kakashi';
import { TEquityInfoKS } from '@commenda/types/src/incorporation/types';
import { TSetNotification } from '@commenda/types/src/random';
import { Prisma, StakeholderType } from '@prisma/client';
import { themeColors } from '../../EquityInfo/common';
import ChartValidationError from '../../EquityInfo/EquitySplitChartError';
import EquitySplitDonutChart from '../../EquityInfo/EquitySplitDonutChart';
import { TShareholderVsSharePercentageMap } from '../../EquityInfo/types';
import CorporateShareholderEquitySplitInput from './EquitySplitInput';

type CorporateShareholderEquitySplitDistributionProps = {
  selectedStakeholders: TShareholderVsSharePercentageMap;
  setSelectedStakeholders: React.Dispatch<
    React.SetStateAction<TShareholderVsSharePercentageMap>
  >;
  equityInfo: TEquityInfoKS;
  onChangeShareholder: (val: string[]) => void;
  onEditShareholderDetails: (stakeholderId: string) => void;
  stakeholderIdsWithError?: string[];
  isDecimalEquityPercentageAllowed?: boolean;
  setNotification: TSetNotification;
  disabled?: boolean;
  setCapTableTransactionIdsToDelete: React.Dispatch<
    React.SetStateAction<string[]>
  >;
};

export const CorporateShareholderEquitySplitDistribution = ({
  selectedStakeholders,
  setSelectedStakeholders,
  equityInfo,
  onChangeShareholder,
  onEditShareholderDetails,
  stakeholderIdsWithError,
  isDecimalEquityPercentageAllowed = true,
  setNotification,
  disabled = false,
  setCapTableTransactionIdsToDelete,
}: CorporateShareholderEquitySplitDistributionProps) => {
  const getEquitySplitData = () => {
    let chartData = [];
    if (selectedStakeholders) {
      chartData = Object.values(selectedStakeholders).map((each) => ({
        name: each.name,
        sharePercentage: each.sharePercentage,
      }));
    }
    return chartData;
  };

  const totalPercentageEntered = Object.values(selectedStakeholders || {})
    .reduce((acc, each) => {
      return acc.plus(new Prisma.Decimal(each.sharePercentage || 0));
    }, new Prisma.Decimal(0))
    .toDecimalPlaces(4)
    .toNumber();

  const onEquityAmountChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    stakeholderId: string,
    type: StakeholderType
  ) => {
    const isValid =
      e.target.value.length === 0 ||
      (!Number.isNaN(parseFloat(e.target.value)) &&
        (isDecimalEquityPercentageAllowed
          ? /^\d*\.?\d{0,4}$/.test(e.target.value) // Allow up to 4 decimal places
          : /^[0-9]+$/.test(e.target.value)));

    if (!isValid) return;

    const currentValue = e.target.value;

    if (currentValue.includes('.')) {
      const [intPart, decimalPart] = currentValue.split('.');
      const formattedIntPart = intPart.replace(/^0+/, '') || '0';
      const formattedDecimalPart = decimalPart.slice(0, 4); // Limit to 4 decimal places
      e.target.value = `${formattedIntPart}.${formattedDecimalPart}`;
    } else {
      // For non-decimal numbers, remove leading zeros but allow a single zero
      e.target.value = currentValue.replace(/^0+/, '') || '0';
    }

    setSelectedStakeholders((prev) => ({
      ...prev,
      [stakeholderId]: {
        ...selectedStakeholders[stakeholderId],
        sharePercentage: new Prisma.Decimal(e.target.value).toNumber(),
        shareCount: equityInfo.shareCount
          ? new Prisma.Decimal(equityInfo.shareCount)
              .mul(new Prisma.Decimal(e.target.value))
              .div(100)
              .round()
              .toNumber()
          : 0,
        type,
      },
    }));
  };

  return (
    <div className="flex items-center gap-6">
      <div className="flex flex-col items-center gap-4">
        <div className="w-40 h-40 flex flex-col gap-6 items-center justify-center">
          {selectedStakeholders ? (
            <EquitySplitDonutChart
              owners={getEquitySplitData()}
              colors={themeColors}
              placeholderColor="#E5E5E5"
            />
          ) : null}
        </div>
        {selectedStakeholders ? (
          <ChartValidationError
            directorVsSharePercentage={Object.entries(selectedStakeholders).map(
              ([key, value]) => ({
                name: selectedStakeholders[key].name,
                sharePercentage: selectedStakeholders[key].sharePercentage,
              })
            )}
            value={parseFloat(totalPercentageEntered.toFixed(3))}
          />
        ) : null}
      </div>
      <div className="flex flex-col w-full">
        {Object.keys(selectedStakeholders || {}).map((stakeholderId, idx) => {
          const [type] = stakeholderId.split('_');

          return (
            <CorporateShareholderEquitySplitInput
              key={stakeholderId}
              isDecimalEquityPercentageAllowed={
                isDecimalEquityPercentageAllowed
              }
              selectedStakeholders={selectedStakeholders}
              stakeholderId={stakeholderId}
              idx={idx}
              type={type as StakeholderType}
              onEquityAmountChange={onEquityAmountChange}
              onEditShareholderDetails={onEditShareholderDetails}
              stakeholderIdsWithError={stakeholderIdsWithError}
              setNotification={setNotification}
              onChangeShareholder={onChangeShareholder}
              disabled={disabled}
              setCapTableTransactionIdsToDelete={
                setCapTableTransactionIdsToDelete
              }
            />
          );
        })}

        <div className="flex justify-between items-center pt-2.5 px-4 pr-[96px]">
          <span className="font-medium">Total</span>
          <div className="w-fit">
            <Input
              type="number"
              className="w-28 hide-arrow"
              value={totalPercentageEntered}
              rightEl={() => <span className="translate-x-1">%</span>}
              error={
                totalPercentageEntered > 100
                  ? 'Total percentage cannot exceed 100%.'
                  : null
              }
              showErrorMessage={false}
              disabled
              readOnly
            />
          </div>
        </div>
      </div>
    </div>
  );
};
